import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, RotateCcw, Star, Check, Lock, AlertCircle } from 'lucide-react';
import { listModelPhotos, getCreatorPageConfig, saveCreatorPageConfig } from '../api/models';
import { AdminModelView } from '../api/types';
import { pickImageUrl } from '../utils/imageUrls';

/**
 * Per-creator public-page configuration:
 *  - which gallery photos are "unlocked" (shown clear, not blurred);
 *  - which related categories appear in the "More like" block.
 * Empty selections fall back to defaults (teaser heuristic / all model tags).
 * The main/hero photo is the model's avatar (managed in the Images tab).
 */
const CreatorPageSettings: React.FC<{ model: AdminModelView }> = ({ model }) => {
  const modelId = model.id;
  const qc = useQueryClient();

  const photosQ = useQuery({ queryKey: ['model-photos', modelId], queryFn: () => listModelPhotos(modelId), enabled: Number.isFinite(modelId) });
  const configQ = useQuery({ queryKey: ['creator-page', modelId], queryFn: () => getCreatorPageConfig(modelId), enabled: Number.isFinite(modelId) });

  const [unlocked, setUnlocked] = useState<Set<number>>(new Set());
  const [cats, setCats] = useState<string[]>([]);

  // Seed local selection from the saved config.
  useEffect(() => {
    if (configQ.data) {
      setUnlocked(new Set(configQ.data.unlockedPhotoIds ?? []));
      setCats(configQ.data.categoryTagSlugs ?? []);
    }
  }, [configQ.data]);

  const photos = (photosQ.data ?? []).filter(p => !p.deleted);
  const avatar = photos.find(p => p.current);
  const galleryPhotos = photos.filter(p => !p.current); // avatar is the main, excluded from the gallery
  const modelTags = model.tags ?? [];

  const dirty = useMemo(() => {
    const cfg = configQ.data;
    if (!cfg) return false;
    const a = [...unlocked].sort((x, y) => x - y).join(',');
    const b = [...(cfg.unlockedPhotoIds ?? [])].sort((x, y) => x - y).join(',');
    return a !== b || cats.join(',') !== (cfg.categoryTagSlugs ?? []).join(',');
  }, [unlocked, cats, configQ.data]);

  const saveMut = useMutation({
    mutationFn: () => saveCreatorPageConfig(modelId, { unlockedPhotoIds: [...unlocked], categoryTagSlugs: cats }),
    onSuccess: (saved) => {
      qc.setQueryData(['creator-page', modelId], saved);
      setUnlocked(new Set(saved.unlockedPhotoIds ?? []));
      setCats(saved.categoryTagSlugs ?? []);
    },
  });

  const togglePhoto = (id: number) => setUnlocked(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const toggleCat = (slug: string) => setCats(prev => prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug]);

  const reset = () => {
    if (configQ.data) {
      setUnlocked(new Set(configQ.data.unlockedPhotoIds ?? []));
      setCats(configQ.data.categoryTagSlugs ?? []);
    }
  };

  if (photosQ.isLoading || configQ.isLoading) {
    return <div className="hf-card"><div className="hf-card-body" style={{ textAlign: 'center', color: 'var(--fg-3)' }}>Loading…</div></div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Gallery photos */}
      <div className="hf-card">
        <div className="hf-card-header">
          <h3 className="hf-card-title">Gallery photos</h3>
          <span className="hf-count">{unlocked.size} unlocked</span>
        </div>
        <div className="hf-card-body">
          <p className="hf-help" style={{ marginTop: 0 }}>
            Click a photo to <strong>unlock</strong> it (shown clear on the creator page). Locked photos appear blurred as a teaser.
            {unlocked.size === 0 && ' No selection → default teaser (first photos clear, rest blurred).'}
          </p>

          {avatar && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 0 14px' }}>
              <div style={{ position: 'relative', width: 56, height: 56, borderRadius: 8, overflow: 'hidden', flex: 'none', background: 'var(--bg-3)' }}>
                {pickImageUrl(avatar.imageUrls, avatar.fileName) && (
                  <img src={pickImageUrl(avatar.imageUrls, avatar.fileName)!} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                )}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}><Star size={13} style={{ color: 'var(--accent)' }} /> Main photo (avatar)</div>
                <div className="hf-help" style={{ marginTop: 2 }}>Always shown. Change it in the Images tab.</div>
              </div>
            </div>
          )}

          {galleryPhotos.length === 0 ? (
            <div className="hf-banner"><AlertCircle size={14} /> No extra gallery photos. Only the avatar will show (plus blurred teasers).</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 10 }}>
              {galleryPhotos.map(p => {
                const url = pickImageUrl(p.imageUrls, p.fileName);
                const isUnlocked = unlocked.has(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => togglePhoto(p.id)}
                    title={isUnlocked ? 'Unlocked — click to lock' : 'Locked — click to unlock'}
                    style={{
                      position: 'relative', aspectRatio: '3 / 4', borderRadius: 10, overflow: 'hidden', padding: 0, cursor: 'pointer',
                      border: `2px solid ${isUnlocked ? 'var(--accent)' : 'transparent'}`, background: 'var(--bg-3)',
                    }}
                  >
                    {url && (
                      <img src={url} alt={p.fileName} loading="lazy"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', filter: isUnlocked ? 'none' : 'blur(4px)', transform: isUnlocked ? 'none' : 'scale(1.06)' }} />
                    )}
                    <span style={{
                      position: 'absolute', top: 6, right: 6, width: 22, height: 22, borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: isUnlocked ? 'var(--accent)' : 'rgba(20,16,26,.6)', color: '#fff',
                    }}>
                      {isUnlocked ? <Check size={13} /> : <Lock size={12} />}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Related categories */}
      <div className="hf-card">
        <div className="hf-card-header">
          <h3 className="hf-card-title">Related categories</h3>
          <span className="hf-count">{cats.length} selected</span>
        </div>
        <div className="hf-card-body">
          <p className="hf-help" style={{ marginTop: 0 }}>
            Pick which of the model's tags appear in the "More like" block (up to 5 shown).
            {cats.length === 0 && ' No selection → all tags (fallback).'}
          </p>
          {modelTags.length === 0 ? (
            <div className="hf-banner"><AlertCircle size={14} /> This model has no tags.</div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {modelTags.map(slug => {
                const on = cats.includes(slug);
                return (
                  <button key={slug} type="button" onClick={() => toggleCat(slug)}
                    className={`hf-tag ${on ? 'accent' : ''}`}
                    style={{ cursor: 'pointer', border: on ? '1px solid var(--accent)' : '1px solid var(--border-1)', opacity: on ? 1 : 0.7 }}>
                    {on && <Check size={11} style={{ marginRight: 4 }} />}{slug}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Save bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button className="hf-btn primary" onClick={() => saveMut.mutate()} disabled={!dirty || saveMut.isPending}>
          <Save size={14} /> {saveMut.isPending ? 'Saving…' : 'Save'}
        </button>
        <button className="hf-btn" onClick={reset} disabled={!dirty || saveMut.isPending}>
          <RotateCcw size={14} /> Reset
        </button>
        {saveMut.isSuccess && !dirty && <span className="hf-tag success">Saved</span>}
        {saveMut.isError && <span className="hf-tag danger">Save failed</span>}
      </div>
    </div>
  );
};

export default CreatorPageSettings;
